<script>
/* global axios */
/*
 * Account-level setup for campaign revenue tracking. Lives next to the WhatsApp
 * reports because that is where the user notices the revenue block is empty,
 * but the settings themselves apply to every campaign channel.
 *
 * Secrets are write-only: the API returns them masked, and an empty field means
 * "keep what is stored" — so saving the form never wipes a working credential.
 */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */

const PROVIDER_LABELS = {
  generic: 'Genérico (n8n, outro gateway)',
  pagarme: 'Pagar.me',
  stripe: 'Stripe',
};

const PROVIDER_HINTS = {
  generic:
    'Recebe qualquer venda já normalizada. Envie o token no cabeçalho X-SiteUP-Token.',
  pagarme:
    'Cadastre esta URL no painel do Pagar.me. Autentique com usuário e senha (Basic Auth) ou com o token no cabeçalho X-SiteUP-Token.',
  stripe:
    'Cadastre esta URL como endpoint de webhook e cole aqui o signing secret (whsec_...).',
};

const CREDENTIAL_FIELDS = {
  generic: [
    { key: 'token', label: 'Token', secret: true },
    { key: 'basic_user', label: 'Usuário (opcional)', secret: false },
    { key: 'basic_password', label: 'Senha (opcional)', secret: true },
  ],
  pagarme: [
    { key: 'token', label: 'Token', secret: true },
    { key: 'basic_user', label: 'Usuário (Basic Auth)', secret: false },
    { key: 'basic_password', label: 'Senha (Basic Auth)', secret: true },
    { key: 'hmac_secret', label: 'Segredo HMAC (opcional)', secret: true },
  ],
  stripe: [{ key: 'signing_secret', label: 'Signing secret', secret: true }],
};

export default {
  name: 'RevenueTrackingSettings',

  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      notice: null,
      settings: null,
      form: {
        enabled: false,
        currency: 'BRL',
        attribution_window_days: 7,
      },
      providerForm: {},
      revealedSecret: null,
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },

    providers() {
      if (!this.settings) return [];
      return Object.keys(this.settings.providers || {}).map(key => ({
        key,
        label: PROVIDER_LABELS[key] || key,
        hint: PROVIDER_HINTS[key] || '',
        fields: CREDENTIAL_FIELDS[key] || [],
        ...this.settings.providers[key],
      }));
    },
  },

  mounted() {
    this.fetchSettings();
  },

  methods: {
    settingsUrl(suffix = '') {
      return `/api/v1/accounts/${this.accountId}/revenue_tracking_settings${suffix}`;
    },

    applySettings(data) {
      this.settings = data;
      this.form = {
        enabled: data.enabled,
        currency: data.currency,
        attribution_window_days: data.attribution_window_days,
      };
      this.providerForm = Object.keys(data.providers || {}).reduce(
        (acc, key) => {
          acc[key] = { enabled: data.providers[key].enabled };
          (CREDENTIAL_FIELDS[key] || []).forEach(field => {
            acc[key][field.key] = '';
          });
          return acc;
        },
        {}
      );
    },

    async fetchSettings() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(this.settingsUrl());
        this.applySettings(data);
      } catch (err) {
        this.error = 'Não foi possível carregar a configuração.';
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      this.error = null;
      this.notice = null;
      try {
        const { data } = await axios.put(this.settingsUrl(), {
          revenue_tracking: { ...this.form, providers: this.providerForm },
        });
        this.applySettings(data);
        this.notice = 'Configuração salva.';
      } catch (err) {
        this.error = 'Não foi possível salvar a configuração.';
      } finally {
        this.saving = false;
      }
    },

    async rotateSecret(provider) {
      this.error = null;
      this.notice = null;
      try {
        const { data } = await axios.post(this.settingsUrl('/rotate_secret'), {
          provider,
        });
        this.revealedSecret = { provider, secret: data.secret };
        await this.fetchSettings();
      } catch (err) {
        this.error = 'Não foi possível gerar o segredo.';
      }
    },

    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.notice = 'Copiado.';
      } catch (err) {
        this.error = 'Não foi possível copiar.';
      }
    },

    maskedValue(provider, fieldKey) {
      return (
        this.settings?.providers?.[provider]?.credentials?.[fieldKey] || ''
      );
    },
  },
};
</script>

<template>
  <div
    class="rounded-2xl border border-n-weak bg-n-surface-1 p-5 flex flex-col gap-4 text-sm"
  >
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12 m-0">
        Rastreamento de vendas
      </h3>
      <p class="text-xs text-n-slate-11 mt-1 mb-0">
        Conecte seu gateway de pagamento para ver quanto cada campanha faturou.
        A venda é atribuída pelo utm_campaign do link ou, quando não houver UTM,
        pelo telefone ou e-mail que o comprador informou no formulário.
      </p>
    </div>

    <div v-if="loading" class="text-n-slate-11 text-xs">Carregando…</div>

    <template v-else-if="settings">
      <div
        v-if="error"
        class="text-xs text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/20 rounded-xl p-2"
      >
        {{ error }}
      </div>
      <div
        v-if="notice"
        class="text-xs text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/20 rounded-xl p-2"
      >
        {{ notice }}
      </div>

      <label class="flex items-center gap-2 cursor-pointer">
        <input v-model="form.enabled" type="checkbox" class="!mb-0" />
        <span class="text-n-slate-12">Ativar rastreamento nesta conta</span>
      </label>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">Moeda</span>
          <input v-model="form.currency" type="text" maxlength="3" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">
            Janela de atribuição (dias)
          </span>
          <input
            v-model.number="form.attribution_window_days"
            type="number"
            min="1"
            max="90"
          />
          <span class="text-[0.6875rem] text-n-slate-11">
            Uma venda sem UTM só é creditada se a campanha tiver sido enviada
            para aquele contato dentro desse prazo.
          </span>
        </label>
      </div>

      <div
        v-for="provider in providers"
        :key="provider.key"
        class="rounded-xl border border-n-weak p-3 flex flex-col gap-2"
      >
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="providerForm[provider.key].enabled"
            type="checkbox"
            class="!mb-0"
          />
          <span class="font-semibold text-n-slate-12">{{
            provider.label
          }}</span>
        </label>
        <p class="text-[0.6875rem] text-n-slate-11 m-0">{{ provider.hint }}</p>

        <div class="flex items-center gap-2">
          <input
            :value="provider.webhook_url"
            type="text"
            readonly
            class="!mb-0 font-mono text-xs"
          />
          <button
            class="px-2 py-1 rounded-xl border border-n-weak text-xs text-n-slate-12 flex-shrink-0"
            @click="copy(provider.webhook_url)"
          >
            Copiar
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <label
            v-for="field in provider.fields"
            :key="field.key"
            class="flex flex-col gap-1"
          >
            <span class="text-xs text-n-slate-11">{{ field.label }}</span>
            <input
              v-model="providerForm[provider.key][field.key]"
              type="text"
              :placeholder="
                maskedValue(provider.key, field.key) || 'não configurado'
              "
              class="!mb-0 font-mono text-xs"
            />
          </label>
        </div>

        <button
          class="self-start px-2 py-1 rounded-xl border border-n-weak text-xs text-n-slate-12"
          @click="rotateSecret(provider.key)"
        >
          Gerar novo segredo
        </button>

        <div
          v-if="revealedSecret && revealedSecret.provider === provider.key"
          class="text-xs bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded-xl p-2 flex flex-col gap-1"
        >
          <span>Copie agora — este valor não será exibido de novo:</span>
          <code class="font-mono break-all">{{ revealedSecret.secret }}</code>
        </div>
      </div>

      <button
        class="self-start px-3 py-1.5 rounded-xl bg-n-blue-9 text-white text-xs font-semibold disabled:opacity-60"
        :disabled="saving"
        @click="save"
      >
        {{ saving ? 'Salvando…' : 'Salvar' }}
      </button>
    </template>
  </div>
</template>
